import { Link, useParams } from "react-router-dom";
import Seo, { breadcrumbLd, faqLd } from "../lib/seo";
import { SITE } from "../lib/site";
import { BLOG, PRODUCTS } from "../data/content";
import { Breadcrumbs } from "../components/site/Layout";
import { PageHero, CtaBand, RelatedGrid } from "../components/site/Sections";
import { Reveal, Overline } from "../components/site/Reveal";
import NotFound from "./NotFound";

const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export function Blog() {
  return (
    <>
      <Seo title="Hologram & Security Label Insights | NanoMax Blog"
        description="Guides and insights on hologram stickers, security labels, tamper evidence, QR authentication and product protection for UAE businesses."
        path="/blog" jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }])} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Blog" }]} />
      <PageHero overline="Blog" title="Hologram & Security Label Insights"
        subtitle="Practical guides on hologram stickers, security labels and product authentication for businesses across the UAE." />
      <section className="px-6 md:px-10 lg:px-16 py-20 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {BLOG.map((b, i) => (
          <Reveal key={b.slug} delay={i * 0.04}>
            <Link to={`/blog/${b.slug}`} className="card grayscale-hover group block h-full overflow-hidden" data-testid={`blog-card-${i}`}>
              <div className="aspect-[16/10] overflow-hidden"><img src={b.image} alt={b.alt} className="w-full h-full object-cover" loading="lazy" /></div>
              <div className="p-6">
                <div className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-holo-cyan">{b.category} · {fmt(b.date)}</div>
                <h2 className="font-head text-lg font-semibold mt-3 group-hover:text-holo-cyan transition-colors leading-snug">{b.title}</h2>
                <p className="text-sm text-[#8A8A9B] mt-3 leading-relaxed">{b.excerpt}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>
      <CtaBand sourcePage="/blog" />
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const b = BLOG.find((x) => x.slug === slug);
  if (!b) return <NotFound />;
  const path = `/blog/${b.slug}`;
  const paras = b.body && b.body.length ? b.body : [b.excerpt, "This article is being expanded. In the meantime, our team is happy to answer any questions about hologram stickers and security labels for your UAE business.", "Contact NanoMax to discuss custom security labels designed around your product, packaging and authentication requirements."];
  const articleLd = {
    "@context": "https://schema.org", "@type": "Article", headline: b.title, image: b.image,
    datePublished: b.date, author: { "@type": "Organization", name: b.author }, publisher: { "@type": "Organization", name: SITE.legal },
  };
  return (
    <>
      <Seo title={`${b.title} | NanoMax Holo Solutions`} description={b.excerpt} path={path} image={b.image} type="article"
        jsonLd={[articleLd, breadcrumbLd([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: b.title, path }])]} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: b.category }]} />
      <article className="px-6 md:px-10 lg:px-16 pt-8 pb-16 max-w-3xl mx-auto">
        <Reveal>
          <div className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-holo-cyan">{b.category} · {fmt(b.date)} · {b.author}</div>
          <h1 className="font-head font-black tracking-tighter leading-[1.02] text-3xl md:text-4xl lg:text-5xl mt-5">{b.title}</h1>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card overflow-hidden mt-10"><img src={b.image} alt={b.alt} className="w-full aspect-[16/9] object-cover" loading="eager" /></div>
        </Reveal>
        <div className="mt-10 space-y-6">
          {paras.map((p, i) => <p key={i} className="text-[#C9C9D4] text-base md:text-lg leading-relaxed">{p}</p>)}
        </div>
        <div className="mt-12 card p-6">
          <Overline>Explore</Overline>
          <div className="mt-4 flex flex-wrap gap-4">
            <Link to="/products" className="link-arrow">View Products <span>&rarr;</span></Link>
            <Link to="/solutions" className="link-arrow">Security Solutions <span>&rarr;</span></Link>
            <Link to="/contact" className="link-arrow">Get a Quote <span>&rarr;</span></Link>
          </div>
        </div>
      </article>
      <RelatedGrid title="Related Products" items={PRODUCTS.slice(0, 4).map((x) => ({ name: x.name, desc: x.tile, to: `/products/${x.slug}` }))} />
      <CtaBand sourcePage={path} />
    </>
  );
}
